// FileSurface — the file's ONE viewing grammar, extracted from FilesLibrary so
// every host reads alike (FRONTEND-PATTERNS §Files: the standard peek on a
// narrow screen — the modal sheet; the stage beside the rail panel wide; the
// stage alone expanded). Consumers: the library (site · asset tabs) and the
// product peek's documents (a catalog artifact opens here as a synthetic
// catalog row — preview + download only); the person section inherits it.
//
// The component owns the surface's own state — the wide media query, Expand,
// and the keyboard walk (arrows · j/k · Enter · N · Escape, standing down over
// editable targets) — while the LENS stays the host's: `file`, its stage `preview`,
// the pager and `onStep` arrive as props, answer handlers arrive already advance-wrapped
// where a walk exists. Unmounting on close resets Expand by construction;
// a step never remounts (the file prop swaps in place — DESIGN-TOKENS §Motion).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '../ui/IconButton.js';
import { Modal } from '../ui/Modal.js';
import { icons } from '../ui/icons.js';
import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import { formatDate, formatNumber } from '../../lib/format.js';
import { isEditable } from '../../lib/keyOwner.js';
import { FilePanel, type FilePanelHandlers } from './FilePanel.js';
import { FileStage } from './FileStage.js';
import type { PickerTopic, WhatItShowsValue } from './WhatItShowsPicker.js';
import type { AssetCategory } from '@ems-app/schemas/assets/categories';
import { fileTitle, formatDuration, hiddenSubjectsOf, namedSubjects, needsReview, proposalOf, useTopicLabel, type CollectionBy, type LibraryFile, type StagePreview } from './filesDisplay.js';

export interface FileSurfaceProps extends FilePanelHandlers {
  file: LibraryFile;
  /** The place the surface sits over — the wide face's WHERE line; a file whose
   *  own anchor differs shows its home in Facts (away-from-anchor). */
  anchorLabel: string;
  awayFromAnchor?: boolean;
  /** browse, or the review walk (the panel leads with the decision; Enter/N answer). */
  mode?: 'browse' | 'review';
  /** The walk's progress (review mode). */
  progress?: { index: number; total: number };
  /** The lens's pager — the footer's "n of m ‹ ›" on both hosts; absent = a lone file. */
  pager?: { onPrev: () => void; onNext: () => void; prevDisabled?: boolean; nextDisabled?: boolean; position?: { index: number; total: number } };
  /** Walk the lens (the keys, the stage's swipes and arrows). */
  onStep?: (delta: number) => void;
  onClose: () => void;
  /** The stage's source for the open file — the host's per-open mint (`useStageLink`). */
  preview: StagePreview;
  onSourceError?: (file: LibraryFile) => void;
  onSourceExpired?: (file: LibraryFile) => void;
  /** Other versions (documents): the family minus this file; the current one's id. */
  versions?: LibraryFile[];
  currentVersionId?: string | null;
  /** The one question's material (the panel's picker): the project, the site whose units it offers (null = none), the kinds present. */
  projectId: string;
  siteId: string | null;
  kinds?: readonly AssetCategory[];
  topicOptions?: PickerTopic[];
  previousAnswer?: { label: string; value: WhatItShowsValue } | null;
  currentUserId?: string;
  canNote?: boolean;
  canWrite: boolean;
  canReview: boolean;
  canRestore?: boolean;
  onOpenSubjectCollection?: (by: CollectionBy, value: string) => void;
  timeZone?: string;
  /** At/above this the surface is the stage beside the panel; below it the panel is the page. Default md. */
  wideQuery?: string;
}

export function FileSurface(props: FileSurfaceProps) {
  const {
    file,
    anchorLabel,
    awayFromAnchor = false,
    mode = 'browse',
    progress,
    pager,
    onStep,
    onClose,
    preview,
    onSourceError,
    onSourceExpired,
    versions = [],
    currentVersionId = null,
    projectId,
    siteId,
    kinds = [],
    topicOptions = [],
    previousAnswer = null,
    currentUserId = '',
    canNote = false,
    canWrite,
    canReview,
    canRestore = false,
    timeZone,
    wideQuery = '(min-width: 48rem)',
  } = props;
  const { t } = useTranslation('files');
  const wide = useMediaQuery(wideQuery);
  const topicLabel = useTopicLabel();
  // The expanded stage belongs to one open of the surface: the host unmounts
  // this component when the surface closes, so the next open starts on the
  // panel — never full screen by default. A step swaps `file` in place.
  const [expanded, setExpanded] = useState(false);
  const review = mode === 'review';
  const step = (delta: number) => onStep?.(delta);

  // ── the keys: the same walk on both layouts, while the surface is open ──
  // The handlers arrive advance-wrapped where a walk exists, so Enter/N answer
  // AND move on exactly as the buttons do.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || isEditable(e.target)) return;
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
        case 'k':
          if (expanded && e.key !== 'k') return; // the stage owns the arrows when expanded
          e.preventDefault();
          step(-1);
          break;
        case 'ArrowRight':
        case 'ArrowDown':
        case 'j':
          if (expanded && e.key !== 'j') return;
          e.preventDefault();
          step(1);
          break;
        case 'Escape':
          if (expanded) setExpanded(false);
          else onClose();
          break;
        case 'Enter': {
          // Enter = the section's primary answer: Yes on a suggestion; Keep as
          // is on a classified capture that waits under the policy.
          if (!(canReview || canWrite)) break;
          const p = proposalOf(file);
          if (p) {
            e.preventDefault();
            props.onConfirm?.(file, p);
          } else if (review && needsReview(file) && (namedSubjects(file).length > 0 || hiddenSubjectsOf(file).length > 0 || file.topics.length > 0)) {
            e.preventDefault();
            props.onNothing?.(file);
          }
          break;
        }
        case 'n':
        case 'N':
          // The answer row's N — wherever the row shows (the lens advances the walk; a browse stays).
          if ((canReview || canWrite) && needsReview(file)) {
            e.preventDefault();
            props.onNothing?.(file);
          }
          break;
        default:
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the handlers close over the host's walk
  }, [file, expanded, review, canReview, canWrite, props.onConfirm, props.onNothing, onStep, onClose]);

  // The wide face: its top LINE is the panel chrome (the same for every kind of
  // file — a photo and a PDF share one header) and only the stage pane is dark,
  // for a picture; expanded to full screen the whole face goes dark. A
  // document's card and the PDF frame beside the rail sit on the light surface,
  // and the stage's ‹ › show in full screen only (the footer's pager walks).
  const picture = file.family === 'image' || file.family === 'video';
  const darkFace = expanded;
  const title = fileTitle(file, topicLabel, {
    voiceNote: t('tile.voiceNote', { duration: formatDuration(file.media?.duration_ms) }),
    moment: formatDate(file.captured_at, 'datetime', timeZone ? { timeZone } : {}) ?? undefined,
  });
  const position = pager?.position;

  if (!wide && !expanded) {
    return (
      <FilePanel
        file={file}
        host="peek"
        open
        wideQuery={wideQuery}
        awayFromAnchor={awayFromAnchor}
        mode={mode}
        progress={progress}
        hero={<FileStage file={file} src={preview.src} srcExpiresAt={preview.expiresAt} failed={preview.failed} mode="hero" onExpand={() => setExpanded(true)} onSwipePrev={() => step(-1)} onSwipeNext={() => step(1)} onDownload={props.onDownload} onSourceError={() => onSourceError?.(file)} onSourceExpired={onSourceExpired && (() => onSourceExpired(file))} />}
        canWrite={canWrite}
        canReview={canReview}
        canRestore={canRestore}
        onOpenSubjectCollection={props.onOpenSubjectCollection}
        versions={versions}
        currentVersionId={currentVersionId}
        onOpenVersion={props.onOpenVersion}
        projectId={projectId}
        siteId={siteId}
        kinds={kinds}
        topicOptions={topicOptions}
        previousAnswer={previousAnswer}
        currentUserId={currentUserId}
        canNote={canNote}
        onClose={onClose}
        pager={pager}
        timeZone={timeZone}
        {...panelHandlersOf(props)}
      />
    );
  }
  return (
    <Modal
      open
      fullscreen
      title={title}
      onClose={() => (expanded ? setExpanded(false) : onClose())}
      className={darkFace ? 'bg-[var(--color-bg-stage)] text-[var(--color-on-stage)]' : 'bg-[var(--color-canvas)] text-[var(--color-text-primary)]'}
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className={`flex min-h-12 items-center gap-2 px-3 py-2 ${darkFace ? '' : 'border-b border-[var(--color-border)] bg-[var(--color-chrome)]'}`}>
          {/* The line answers WHERE, never WHAT (the rail carries the file): ONE
              way out — the back door (Escape closes too) — then the place;
              in full screen the rail is gone, so the line takes the file's identity. */}
          <IconButton aria-label={t('stage.back')} onClick={onClose} variant={darkFace ? 'stage' : 'ghost'}>
            <icons.caretLeft size={20} aria-hidden className="rtl:rotate-180" />
          </IconButton>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">{expanded ? title : anchorLabel}</div>
            <div className={`truncate text-xs ${darkFace ? 'text-[var(--color-on-stage-muted)]' : 'text-[var(--color-text-secondary)]'}`}>{expanded ? anchorLabel : t('stage.files')}</div>
          </div>
          {/* The position rides the panel's pager; when the panel is hidden (Expand) the stage line carries it. */}
          {expanded && position && (
            <span className={`text-xs ${darkFace ? 'text-[var(--color-on-stage-muted)]' : 'text-[var(--color-text-secondary)]'}`}>{t('stage.position', { index: formatNumber(position.index), total: formatNumber(position.total) })}</span>
          )}
          <IconButton aria-label={expanded ? t('stage.showPanel') : t('stage.expand')} onClick={() => setExpanded((x) => !x)} variant={darkFace ? 'stage' : 'ghost'}>
            {expanded ? <icons.collapse size={20} aria-hidden /> : <icons.expand size={20} aria-hidden />}
          </IconButton>
        </div>
        <div className="flex min-h-0 flex-1">
          <div className="min-w-0 flex-1">
            <FileStage
              file={file}
              src={preview.src}
              srcExpiresAt={preview.expiresAt}
              failed={preview.failed}
              mode="full"
              stage={expanded || picture}
              arrows={expanded}
              onSwipePrev={pager && !pager.prevDisabled ? () => step(-1) : undefined}
              onSwipeNext={pager && !pager.nextDisabled ? () => step(1) : undefined}
              onDownload={props.onDownload}
              onSourceError={() => onSourceError?.(file)}
              onSourceExpired={onSourceExpired && (() => onSourceExpired(file))}
            />
          </div>
          {!expanded && (
            <div className="w-[min(26rem,40vw)] shrink-0 text-[var(--color-text-primary)]">
              <FilePanel
                file={file}
                host="rail"
                awayFromAnchor={awayFromAnchor}
                mode={mode}
                progress={progress}
                canWrite={canWrite}
                canReview={canReview}
                canRestore={canRestore}
                onOpenSubjectCollection={props.onOpenSubjectCollection}
                versions={versions}
                currentVersionId={currentVersionId}
                onOpenVersion={props.onOpenVersion}
                projectId={projectId}
                siteId={siteId}
                kinds={kinds}
                topicOptions={topicOptions}
                previousAnswer={previousAnswer}
                currentUserId={currentUserId}
                canNote={canNote}
                pager={pager}
                showKeys
                timeZone={timeZone}
                className="h-full"
                {...panelHandlersOf(props)}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** The handler subset FilePanel spreads — picked so the surface's own props
 *  (file, pager, …) never shadow the panel's. */
function panelHandlersOf(props: FileSurfaceProps): FilePanelHandlers {
  const {
    onConfirm, onReclassify, onNothing, onDelete, onRestore, onDownload, onOpenInTab, onRename, onEditCaption,
    onMove, onReplace, onOpenAtAnchor, onOpenSubject, onOpenVersion, onOpenSubjectCollection, onCopyLink,
    onAddNote, onEditNote, onDeleteNote,
  } = props;
  return {
    onConfirm, onReclassify, onNothing, onDelete, onRestore, onDownload, onOpenInTab, onRename, onEditCaption,
    onMove, onReplace, onOpenAtAnchor, onOpenSubject, onOpenVersion, onOpenSubjectCollection, onCopyLink,
    onAddNote, onEditNote, onDeleteNote,
  };
}
